// File path: src/project-portal/pages/Whatsapp/BatchLogs.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
  styled,
  Tooltip,
  TextField,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorIcon from '@mui/icons-material/Error';
import MessageIcon from '@mui/icons-material/Message';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { BatchLogsAPI } from '../../../api/Endpoints/BatchLogs';

// Styled components for log-like appearance
const LogTimestamp = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  fontWeight: 400,
}));

const LogBatchCode = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  marginBottom: '2px',
}));

const LogContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
}));

const LogTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 0,
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
  },
}));

const LogHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
}));

const LogStatusChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 20,
  minWidth: 80,
  textTransform: 'capitalize',
}));

const LogRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child .MuiTableCell-root': {
    borderBottom: 'none',
  },
}));

const LogFont = styled(Typography)(({ theme }) => ({
  fontFamily: 'Monaco, Consolas, "Courier New", monospace',
  fontSize: '0.875rem',
}));

const BatchLogs = () => {
  const navigate = useNavigate();
  const [batchLogs, setBatchLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Set default dates on component mount
  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    // Format dates as YYYY-MM-DD for input fields
    const formattedToday = today.toISOString().split('T')[0];
    const formattedSevenDaysAgo = sevenDaysAgo.toISOString().split('T')[0];
    
    setToDate(formattedToday);
    setFromDate(formattedSevenDaysAgo);
  }, []);

  useEffect(() => {
    // Only fetch if both dates are set
    if (fromDate && toDate) {
      fetchBatchLogs();
    }
  }, [fromDate, toDate]);

  const fetchBatchLogs = async () => {
    try {
      setLoading(true);
      // Fetch all batch logs without pagination
      const params = {
        page: 1,
        limit: 1000 // Set a high limit to fetch all records
      };
      
      // Add date filters only if both dates are selected
      if (fromDate && toDate) {
        params.from = fromDate;
        params.to = toDate;
      }
      
      const response = await BatchLogsAPI.getBatchLogs(params);
      
      if (response.data.success) {
        setBatchLogs(response.data.data || []);
      } else {
        setError('Failed to fetch batch logs');
      }
    } catch (err) {
      setError('Error fetching batch logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (batch) => {
    setSelectedBatch(batch);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedBatch(null);
  };

  const handleViewMessage = (leadId) => {
    navigate(`/dashboard/whatsapp?lead_id=${leadId}`);
    handleCloseModal();
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  // Capitalize first letter of status
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <div className="page-cntr">
      <div className="lead-cntr follo-up-cntr">
        <div className="lead-table lead-page-cntr">
          <div className="title-sec">
            <Card sx={{ display: "flex", justifyContent: "space-between", mb: 3, boxShadow: 'none' }}>
              <PageHeader title={"Whatsapp Batches"} />
            </Card>
          </div>
        </div>

        <LogContainer>
          <CardContent sx={{ padding: 0 }}>
            {/* Header with Icon Legend and Date Pickers - Moved above the table */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
              {/* Icon Legend on the left */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="subtitle2">Responses:</Typography>
                <Tooltip title="Sent Messages">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SendIcon sx={{ fontSize: '1rem', color: '#2196f3' }} />
                    <Typography variant="caption">Sent</Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Delivered Messages">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: '1rem', color: '#4caf50' }} />
                    <Typography variant="caption">Delivered</Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Read Messages">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DoneAllIcon sx={{ fontSize: '1rem', color: '#4caf50' }} />
                    <Typography variant="caption">Read</Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Failed Messages">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ErrorIcon sx={{ fontSize: '1rem', color: '#f44336' }} />
                    <Typography variant="caption">Failed</Typography>
                  </Box>
                </Tooltip>
              </Box>
              
              {/* Date Pickers on the right */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="From"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                />
                <TextField
                  label="To"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                />
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={fetchBatchLogs}
                >
                  Filter
                </Button>
              </Box>
            </Box>
            
            <LogTableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <LogRow>
                    <LogHeaderCell>Sl No</LogHeaderCell>
                    <LogHeaderCell>
                      Batch Information
                    </LogHeaderCell>
                    <LogHeaderCell>Total Messages</LogHeaderCell>
                    <LogHeaderCell>Responses</LogHeaderCell>
                    <LogHeaderCell>Batch Status</LogHeaderCell>
                    <LogHeaderCell>Actions</LogHeaderCell>
                  </LogRow>
                </TableHead>
                <TableBody>
                  {batchLogs.map((batch, index) => (
                    <LogRow key={batch.batch_id}>
                      <TableCell>
                        <LogFont>
                          {index + 1}
                        </LogFont>
                      </TableCell>
                      <TableCell>
                        <LogBatchCode>{batch.batch_code}</LogBatchCode>
                        <LogTimestamp>{formatTimestamp(batch.created_at)}</LogTimestamp>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,}}>
                          <MessageIcon sx={{ fontSize: '1rem', color: '#2196f3' }} />
                          <LogFont>{batch.total_messages || 0}</LogFont>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Tooltip title="Sent Messages">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SendIcon 
                                sx={{ 
                                  fontSize: '1rem', 
                                  color: '#2196f3',
                                  opacity: batch.sent_count > 0 ? 1 : 0.5
                                }} 
                              />
                            
                                <LogFont sx={{
                                    opacity: batch.sent_count > 0 ? 1 : 0.5
                                }}>{batch.sent_count}</LogFont>
                            
                            </Box>
                          </Tooltip>
                          <Tooltip title="Delivered Messages">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircleIcon 
                                sx={{ 
                                  fontSize: '1rem', 
                                  color: '#4caf50',
                                  opacity: batch.delivered_count > 0 ? 1 : 0.5
                                }} 
                              />
                             
                                <LogFont sx={{
                                    opacity: batch.delivered_count > 0 ? 1 : 0.5
                                }}>{batch.delivered_count}</LogFont>
                             
                            </Box>
                          </Tooltip>
                          <Tooltip title="Read Messages">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DoneAllIcon 
                                sx={{ 
                                  fontSize: '1rem', 
                                  color: '#4caf50',
                                  opacity: batch.read_count > 0 ? 1 : 0.5
                                }} 
                              />
                             
                                <LogFont sx={{
                                    opacity: batch.read_count > 0 ? 1 : 0.5
                                
}}>{batch.read_count}</LogFont>
                              
                            </Box>
                          </Tooltip>
                          <Tooltip title="Failed Messages">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ErrorIcon 
                                sx={{ 
                                  fontSize: '1rem', 
                                  color: '#f44336',
                                  opacity: batch.failed_count > 0 ? 1 : 0.5
                                }} 
                              />
                             
                                <LogFont 
                                sx={{
                                    opacity: batch.failed_count > 0 ? 1 : 0.5

                                }}
                                >{batch.failed_count}</LogFont>
                            
                            </Box>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <LogStatusChip 
                          label={capitalizeFirstLetter(batch.status) || 'Unknown'} 
                          color={getStatusColor(batch.status)} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          color="primary" 
                          onClick={() => handleViewDetails(batch)}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </LogRow>
                  ))}
                </TableBody>
              </Table>
            </LogTableContainer>
            
            {batchLogs.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No Batch Logs found
                </Typography>
              </Box>
            )}
          </CardContent>
        </LogContainer>

        {/* Batch Details Modal */}
        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
          <DialogTitle>
            Batch Details: {selectedBatch?.batch_code}
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" gutterBottom>
              Created at: {selectedBatch?.created_at ? formatTimestamp(selectedBatch.created_at) : 'N/A'}
            </Typography>
            
            <LogTableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <LogRow>
                    <LogHeaderCell>Sl No</LogHeaderCell>
                    <LogHeaderCell>Lead Name</LogHeaderCell>
                    <LogHeaderCell>Status</LogHeaderCell>
                    <LogHeaderCell>Phone Number</LogHeaderCell>
                    <LogHeaderCell>Actions</LogHeaderCell>
                  </LogRow>
                </TableHead>
                <TableBody>
                  {selectedBatch?.messages?.map((message, index) => (
                    <LogRow key={message.message_id || index}>
                      <TableCell>
                        <LogFont>
                          {index + 1}
                        </LogFont>
                      </TableCell>
                      <TableCell>
                        <LogFont>
                          {message.lead_name || 'N/A'}
                        </LogFont>
                      </TableCell>
                      <TableCell>
                        <LogStatusChip 
                          label={capitalizeFirstLetter(message.wa_status) || 'Unknown'} 
                          size="small"
                          color={
                            message.wa_status === 'read' ? 'success' :
                            message.wa_status === 'delivered' ? 'info' :
                            message.wa_status === 'failed' ? 'error' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <LogFont>
                          {message.phone_number || 'N/A'}
                        </LogFont>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => handleViewMessage(message.lead_id)}
                          disabled={!message.lead_id}
                        >
                          View Chat
                        </Button>
                      </TableCell>
                    </LogRow>
                  ))}
                </TableBody>
              </Table>
            </LogTableContainer>
            
            {(!selectedBatch?.messages || selectedBatch?.messages?.length === 0) && (
              <Typography variant="body1" align="center" color="text.secondary" sx={{ py: 2 }}>
                No messages in this batch
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default BatchLogs;